import { useState, useEffect } from 'react';
import { JourneyPhase } from '@/hooks/useJourneyProgress';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Heart, Target, Sparkles, Map, CheckCircle2, Users, ArrowRight, X, Mountain } from 'lucide-react';
import { cn } from '@/lib/utils';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Heart,
  Target,
  Sparkles,
  Map,
  CheckCircle2,
  Users,
};

interface JourneyWizardProps {
  phases: JourneyPhase[];
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (tabValue: string) => void;
}

export const JourneyWizard = ({
  phases,
  isOpen,
  onClose,
  onNavigate,
}: JourneyWizardProps) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Find the first incomplete phase to start from
  useEffect(() => {
    if (isOpen) {
      const firstIncomplete = phases.findIndex(p => !p.isComplete);
      setCurrentStep(firstIncomplete >= 0 ? firstIncomplete : 0);
    }
  }, [isOpen, phases]);

  const currentPhase = phases[currentStep];
  const Icon = currentPhase ? iconMap[currentPhase.icon] : Target;

  const handleNext = () => {
    if (currentStep < phases.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGoToPhase = () => {
    if (currentPhase) {
      onNavigate(currentPhase.tabValue);
      onClose();
    }
  };

  if (!currentPhase) return null;

  const phaseDetails: Record<string, { benefits: string[]; actions: string[] }> = {
    values: {
      benefits: [
        'Clarify what truly matters to you',
        'Make decisions aligned with your identity',
        'Build goals that feel meaningful',
      ],
      actions: [
        'Start the AI-guided discovery session',
        'Reflect on questions about your priorities',
        'Save at least 3 core values',
      ],
    },
    goals: {
      benefits: [
        'Create clear direction for growth',
        'Balance learning, networking, and doing',
        'Track progress with measurable milestones',
      ],
      actions: [
        'Set goals across Education, Exposure, Experience',
        'Add habits to break goals into daily actions',
        'Define target dates for accountability',
      ],
    },
    coach: {
      benefits: [
        'Identify skill gaps with AI analysis',
        'Get personalized goal suggestions',
        'Receive coaching based on your progress',
      ],
      actions: [
        'Complete your career profile in Settings',
        'Run a skill gap analysis',
        'Add suggested goals to your list',
      ],
    },
    plan: {
      benefits: [
        'Get a structured growth roadmap',
        'See clear milestones with timeframes',
        'Know exactly what skills to develop',
      ],
      actions: [
        'Generate your AI-powered plan',
        'Review the suggested milestones',
        'Use actions to inform your habits',
      ],
    },
    habits: {
      benefits: [
        'Build consistent daily momentum',
        'Track streaks for motivation',
        'Execute on your development plan',
      ],
      actions: [
        'Add habits linked to your goals',
        'Check off habits daily',
        'Maintain your streaks',
      ],
    },
    mentorship: {
      benefits: [
        'Accelerate growth with guidance',
        'Expand your professional network',
        'Give back by mentoring others',
      ],
      actions: [
        'Set up your mentor profile',
        'Browse AI-matched mentors',
        'Request or offer mentorship',
      ],
    },
  };

  const details = phaseDetails[currentPhase.id] || { benefits: [], actions: [] };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Mountain className="w-5 h-5 text-primary" />
              Your Growth Journey
            </DialogTitle>
          </div>
          <DialogDescription>
            Follow these phases to build a complete career development system.
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center gap-2">
          {phases.map((phase, index) => {
            const PhaseIcon = iconMap[phase.icon];
            return (
              <button
                key={phase.id}
                onClick={() => setCurrentStep(index)}
                className={cn(
                  "flex-1 h-2 rounded-full transition-colors",
                  index === currentStep
                    ? "bg-primary"
                    : phase.isComplete
                      ? "bg-primary/50"
                      : "bg-muted"
                )}
              />
            );
          })}
        </div>

        {/* Current Phase Content */}
        <Card className="border-primary/20">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center",
                currentPhase.isComplete ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"
              )}>
                {currentPhase.isComplete ? (
                  <CheckCircle2 className="w-6 h-6" />
                ) : (
                  <Icon className="w-6 h-6" />
                )}
              </div>
              <div>
                <CardTitle className="text-lg">
                  Phase {currentStep + 1}: {currentPhase.name}
                </CardTitle>
                <CardDescription>{currentPhase.description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Progress */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">{currentPhase.progress}%</span>
              </div>
              <Progress value={currentPhase.progress} className="h-2" />
            </div>

            {/* Benefits */}
            <div>
              <h4 className="text-sm font-medium mb-2">Why it matters:</h4>
              <ul className="space-y-1">
                {details.benefits.map((benefit, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            {/* Actions */}
            <div>
              <h4 className="text-sm font-medium mb-2">What to do:</h4>
              <ul className="space-y-1">
                {details.actions.map((action, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium shrink-0">
                      {i + 1}
                    </span>
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            onClick={handlePrev}
            disabled={currentStep === 0}
          >
            Previous
          </Button>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={handleGoToPhase} className="gap-2">
              Go to {currentPhase.name}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          
          <Button
            variant="ghost"
            onClick={handleNext}
            disabled={currentStep === phases.length - 1}
          >
            Next
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
